import { memo } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useHasAnyRole } from '@/auth/hooks';
import { AppRole } from '@/auth/roles';
import { cn } from '@/lib/utils';
import type { CommentNode, ModerationDetail } from '../types';
import type { ModerationActions } from '../hooks/useModerationActions';
import { formatDateTime, initials } from '../lib/display';

// Recursive comment node (parity ModerationDetailsCommentCard.kt). Reported node highlighted red,
// "Reply @user:" prefix for nested replies, indentation capped at one level (:151-158, :253).
const CommentNodeItem = memo(function CommentNodeItem({
  node,
  depth,
}: {
  node: CommentNode;
  depth: number;
}) {
  return (
    <div className={cn(depth > 0 && 'ml-6 border-l pl-4')}>
      <div
        className={cn(
          'rounded-lg border p-3',
          node.isReported && 'border-destructive bg-destructive/5',
        )}
      >
        <div className="flex items-center gap-2">
          <Avatar className="size-6">
            {node.avatar && <AvatarImage src={node.avatar} alt={node.author} />}
            <AvatarFallback className="text-[10px]">{initials(node.author)}</AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium">{node.author}</span>
          {node.authorUname && <span className="text-xs text-muted-foreground">@{node.authorUname}</span>}
          {node.isReported && <Badge variant="destructive">Reported</Badge>}
          <span className="ml-auto text-xs text-muted-foreground">{formatDateTime(node.date)}</span>
        </div>
        {node.replyToUname && (
          <p className="mt-1 text-xs text-muted-foreground">Reply @{node.replyToUname}:</p>
        )}
        <p className="mt-1 text-sm">{node.text}</p>
        <p className="mt-1 text-xs text-muted-foreground">{node.likes} likes</p>
      </div>
      {node.children.map((child) => (
        <CommentNodeItem key={child.id} node={child} depth={Math.min(depth + 1, 1)} />
      ))}
    </div>
  );
});

/**
 * Reported comment card (doc 09 §4.5). Renders the recursive thread and a destructive Remove-Comment
 * button (real DELETE /comment/{id}; confirm + required reason, no undo).
 */
export function ReportedCommentCard({
  report,
  actions,
}: {
  report: ModerationDetail;
  actions: ModerationActions;
}) {
  const canModerate = useHasAnyRole([AppRole.Administrator, AppRole.SystemAdmin, AppRole.ContentManager]);
  if (report.kind !== 'COMMENT') return null;
  const { comment } = report;
  const canDelete = !!(comment.reportedCommentId ?? comment.tree?.id);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Reported Comment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {comment.postTitle && comment.postTitle !== '—' && (
          <p className="text-xs text-muted-foreground">On post: {comment.postTitle}</p>
        )}
        {comment.tree ? (
          <CommentNodeItem node={comment.tree} depth={0} />
        ) : (
          <p className="text-sm text-muted-foreground">Comment thread is unavailable.</p>
        )}

        {canModerate && (
          <Button
            className="w-full"
            variant="destructive"
            disabled={actions.isPending || !canDelete}
            onClick={() => void actions.deleteComment()}
          >
            Remove Comment
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
